/**
 * Protocolo `pmtiles://` para MapLibre 3 (API de callback).
 *
 * `Protocol#tile` de pmtiles 4 (capa v3compat) llama al callback aunque
 * MapLibre ya haya cancelado el pedido (aborta y responde con AbortError).
 * Si eso pasa con el mapa destruido (`map.remove()` → `map.style` undefined),
 * `VectorTileSource.load` lee `map.style.sourceCaches` y queda un
 * unhandled rejection «Cannot read properties of undefined (reading 'sourceCaches')».
 *
 * Este wrapper solo descarta respuestas de pedidos ya cancelados. Sin cancelación
 * el comportamiento y el valor de retorno son los mismos.
 * @see docs/maps/map-basemap-and-pmtiles.md
 */

/**
 * @param {(params: object, callback: Function) => ({ cancel: Function }|any)} tile
 * @returns {(params: object, callback: Function) => any}
 */
export function createCancelSafeProtocolHandler(tile) {
  return (params, callback) => {
    // API de promesas (AbortController) u otra firma: sin cambios.
    if (typeof callback !== 'function') return tile(params, callback);

    let cancelled = false;
    const request = tile(params, (...args) => {
      if (cancelled) return;
      callback(...args);
    });
    return {
      ...(request && typeof request === 'object' ? request : {}),
      cancel: () => {
        cancelled = true;
        if (request && typeof request.cancel === 'function') request.cancel();
      },
    };
  };
}

let registered = false;

/**
 * Registra `pmtiles://` una sola vez en MapLibre, con el wrapper.
 * @param {{ addProtocol: Function }} maplibre
 * @param {{ tile: Function }} protocol instancia de `pmtiles.Protocol`
 * @returns {boolean} true si se registró ahora
 */
export function registerPmtilesProtocol(maplibre, protocol) {
  if (registered) return false;
  maplibre.addProtocol('pmtiles', createCancelSafeProtocolHandler(protocol.tile.bind(protocol)));
  registered = true;
  return true;
}

/** Solo tests. */
export function resetPmtilesProtocolRegistrationForTests() {
  registered = false;
}
